import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, LogOut, Filter, Printer,
  CheckSquare, Square, Archive, Star, X, Loader2
} from 'lucide-react';
import { screeningAPI, downloadCandidatesWithResumes } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CandidateCard from './CandidateCard';
import Loading from '../Common/Loading';
import Alert from '../Common/Alert';

const Results = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [screening, setScreening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showConfetti, setShowConfetti] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showSelectionPanel, setShowSelectionPanel] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const fetchScreening = useCallback(async () => {
    try {
      const response = await screeningAPI.getById(id);
      setScreening(response.data);
    } catch (err) {
      setError('Failed to load screening details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchScreening();
    setTimeout(() => setShowConfetti(false), 3000);
  }, [fetchScreening]);

  // ENHANCED: Download with actual resumes
  const handleDownloadWithResumes = async () => {
    if (selectedCandidates.length === 0) {
      setError('Please select at least one candidate');
      return;
    }

    try {
      setDownloadLoading(true);
      setError('');

      await downloadCandidatesWithResumes(id, selectedCandidates);

      setSuccess(`Successfully downloaded ${selectedCandidates.length} candidates with resumes!`);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Quick selection methods
const selectTop15 = () => {
  const candidates = screening.topCandidates.slice(0, Math.min(15, screening.topCandidates.length));
  setSelectedCandidates(candidates.map(c => c.id));
  setSuccess(`Selected top ${candidates.length} candidates`);
};

const selectAbove80 = () => {
  const candidates = screening.topCandidates.filter(c => c.matchScore >= 80);
  setSelectedCandidates(candidates.map(c => c.id));
  setSuccess(`Selected ${candidates.length} candidates with 80%+ match`);
};

const selectAbove70 = () => {
  const candidates = screening.topCandidates.filter(c => c.matchScore >= 70);
  setSelectedCandidates(candidates.map(c => c.id));
  setSuccess(`Selected ${candidates.length} candidates with 70%+ match`);
};

const selectAll = () => {
  setSelectedCandidates(screening.topCandidates.map(c => c.id));
  setSuccess(`Selected all ${screening.topCandidates.length} candidates`);
};
  const clearSelection = () => {
    setSelectedCandidates([]);
    setSuccess('Selection cleared');
  };

  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates(prev =>
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const filteredCandidates = screening?.topCandidates.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'high') return c.matchScore >= 80;
    if (filter === 'medium') return c.matchScore >= 60 && c.matchScore < 80;
    if (filter === 'low') return c.matchScore < 60;
    return true;
  }) || [];

  const selectedCount = selectedCandidates.length;

  if (loading) return <Loading />;
  if (!screening) return <div className="text-center p-8">Screening not found</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            >
              {['🎉', '🎊', '⭐', '✨', '🏆'][Math.floor(Math.random() * 5)]}
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-lg shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition no-print"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>
            <div className="flex items-center gap-3 no-print">
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 transition"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={() => setShowSelectionPanel(!showSelectionPanel)}
                className={`px-5 py-2.5 rounded-xl flex items-center gap-2 transition font-semibold shadow-md ${
                  showSelectionPanel
                    ? 'bg-purple-600 text-white shadow-purple-200'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg hover:shadow-green-200 animate-pulse'
                }`}
              >
                <Download className="w-5 h-5" />
                {showSelectionPanel ? 'Selection Mode ON' : 'Select & Download Resumes'}
              </button>
              <button
                onClick={logout}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{screening.jobTitle}</h1>
            <p className="text-gray-600">{screening.topCandidates?.length || 0} candidates • {selectedCount} selected</p>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mt-4 no-print">
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            {['all', 'high', 'medium', 'low'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg transition ${
                  filter === f
                    ? f === 'all' ? 'bg-blue-600 text-white' :
                      f === 'high' ? 'bg-green-600 text-white' :
                      f === 'medium' ? 'bg-yellow-600 text-white' :
                      'bg-orange-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {f === 'all' ? 'All' : f === 'high' ? '80%+' : f === 'medium' ? '60-79%' : '<60%'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

        {/* First-time User Hint */}
        {!showSelectionPanel && selectedCount === 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-green-900">Ready to shortlist candidates?</h3>
                <p className="text-green-700 text-sm">Click "Select & Download Resumes" to pick your top candidates and download their resumes with AI analysis.</p>
              </div>
            </div>
            <button
              onClick={() => setShowSelectionPanel(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition whitespace-nowrap"
            >
              Start Selecting
            </button>
          </div>
        )}

        {/* Selection Panel */}
        {showSelectionPanel && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                  <CheckSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Smart Selection</h3>
                  <p className="text-gray-600 text-sm">{selectedCount} candidate{selectedCount !== 1 ? 's' : ''} selected</p>
                </div>
              </div>
              <button
                onClick={() => setShowSelectionPanel(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-blue-900 mb-2">📦 Download Package Includes:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✓ Actual resume files (PDF/DOCX) with ranking</li>
                <li>✓ Quick ranking summary</li>
                <li>✓ Detailed analysis for each candidate</li>
                <li>✓ CSV file for spreadsheet analysis</li>
              </ul>
            </div>

            {/* Quick Selection Buttons */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <button
                onClick={selectTop15}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-purple-200 rounded-xl hover:border-purple-400 hover:shadow-md transition"
              >
                <Star className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-gray-700">Top 15</span>
              </button>
              <button
                onClick={selectAbove80}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-green-200 rounded-xl hover:border-green-400 hover:shadow-md transition"
              >
                <CheckSquare className="w-4 h-4 text-green-600" />
                <span className="font-medium text-gray-700">80%+ Match</span>
              </button>
              <button
                onClick={selectAbove70}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:shadow-md transition"
              >
                <CheckSquare className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-gray-700">70%+ Match</span>
              </button>
              <button
                onClick={selectAll}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:shadow-md transition"
              >
                <CheckSquare className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-700">Select All</span>
              </button>
              <button
                onClick={clearSelection}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-red-200 rounded-xl hover:border-red-400 hover:shadow-md transition"
              >
                <Square className="w-4 h-4 text-red-600" />
                <span className="font-medium text-gray-700">Clear</span>
              </button>
            </div>

            {/* Download Button */}
            {selectedCount > 0 && (
              <button
                onClick={handleDownloadWithResumes}
                disabled={downloadLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Preparing {selectedCount} Resume{selectedCount !== 1 ? 's' : ''}...
                  </>
                ) : (
                  <>
                    <Archive className="w-5 h-5" />
                    Download {selectedCount} with Resumes
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Results */}
        {filteredCandidates.length > 0 ? (
          <div className="space-y-6">
            {filteredCandidates.map((candidate, index) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                index={index}
                isSelected={selectedCandidates.includes(candidate.id)}
                onToggleSelect={() => toggleCandidateSelection(candidate.id)}
                showCheckbox={showSelectionPanel}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <Filter className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No candidates match the selected filter</p>
            <button onClick={() => setFilter('all')} className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              Show All
            </button>
          </div>
        )}
      </div>

      {/* Floating Action Bar - Shows when candidates are selected */}
      {selectedCount > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 no-print animate-slideUp">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="flex items-center gap-3 px-4 py-2 bg-purple-50 rounded-xl">
              <CheckSquare className="w-5 h-5 text-purple-600" />
              <span className="font-bold text-purple-900">{selectedCount}</span>
              <span className="text-purple-700">selected</span>
            </div>

            <div className="h-8 w-px bg-gray-200" />

            <button
              onClick={clearSelection}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition font-medium"
            >
              Clear
            </button>

            <button
              onClick={handleDownloadWithResumes}
              disabled={downloadLoading}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-green-200 transform hover:-translate-y-0.5 transition flex items-center gap-2 disabled:opacity-50"
            >
              {downloadLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Download {selectedCount} Resume{selectedCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {downloadLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-center mb-2">
              Preparing Your Download
            </h3>
            <p className="text-gray-600 text-center">
              Packaging {selectedCount} resume{selectedCount !== 1 ? 's' : ''} with ranking and analysis...
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes confetti {
          0% { transform: translateY(0) rotateZ(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotateZ(720deg); opacity: 0; }
        }
        .animate-confetti { animation: confetti linear forwards; }
        @keyframes slideUp {
          from { transform: translate(-50%, 100px); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        @media print { .no-print { display: none !important; } }
      `}</style>
    </div>
  );
};

export default Results;